import Keycloak from "keycloak-js";
import React, {
  ReactElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {AppSettingsContext} from "./AppSettingsProvider";

let keycloak = new Keycloak();

/**
 * Context that provide access to a global keycloak object (its not a state so changes won't trigger re-renders) and whether it has been initialized.
 *
 * The authenticated-Context is preferable to keycloak.authenticated, as it will trigger re-renders in some cases.
 */
export const KeycloakContext = React.createContext({
  keycloak,
  initialized: false,
  authenticated: false,
});

function KeycloakProvider({children}: {children: ReactElement}) {
  const {keycloakUrl} = useContext(AppSettingsContext);
  const [initialized, setInitialized] = useState(false);
  const [failedRefreshCount, setFailedRefreshCount] = useState(0);
  const [authLogout, setAuthLogout] = useState(false);
  const refreshRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const isPublicPath = ["/datamarket/imprint"].includes(
      window.location.pathname,
    );
    const redirectUri =
      window.location.origin +
      window.location.pathname +
      window.location.search;

    keycloak = new Keycloak({
      url: keycloakUrl,
      realm: "smartcityhub",
      clientId: "datamarket-application",
    });

    if (!initialized) {
      keycloak.onAuthLogout = () => {
        setAuthLogout(true);
      };
      keycloak
        .init({
          onLoad: isPublicPath ? "check-sso" : "login-required",
          redirectUri,
          silentCheckSsoRedirectUri:
            window.location.origin + "/datamarket/silent-check-sso.html",
          silentCheckSsoFallback: false,
          pkceMethod: "S256",
        })
        .then(() => {
          setAuthLogout(false);
          refreshRef.current = setInterval(() => {
            keycloak
              .updateToken(90)
              .then(() => {
                setFailedRefreshCount(0);
              })
              .catch(() => {
                setFailedRefreshCount((c) => {
                  if (c > 0 && refreshRef.current) {
                    clearInterval(refreshRef.current);
                    keycloak.clearToken();
                  }
                  return c + 1;
                });
              });
          }, 60000);
        })
        .catch(() => {
          setAuthLogout(true);
        })
        .finally(() => {
          setInitialized(true);
        });
    }
    return () => {
      if (refreshRef.current) clearInterval(refreshRef.current);
    };
  }, []);

  return (
    <KeycloakContext.Provider
      value={{
        keycloak: keycloak,
        initialized,
        authenticated: !!(
          initialized &&
          !authLogout &&
          failedRefreshCount <= 2 &&
          keycloak.authenticated
        ),
      }}
    >
      {children}
    </KeycloakContext.Provider>
  );
}

export default KeycloakProvider;
export {keycloak};
